import React, { useState, useEffect } from 'react';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';

const AdminAreas = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortBy, setSortBy] = useState('area_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchAreas();
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await httpClient.get(ENDPOINTS.CITIES);
      let citiesData = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
      setCities(citiesData);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchAreas = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/areas-stats`);
      let areasData = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
      setAreas(areasData);
    } catch (error) {
      console.error('Error fetching areas:', error);
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedAreas = () => {
    let filtered = areas.filter(area => {
      const matchSearch = searchTerm === '' || 
        area.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchCity = cityFilter === '' || 
        area.city_id === parseInt(cityFilter);
      
      const matchDate = dateFilter === '' || 
        (area.created_at && area.created_at.startsWith(dateFilter));
      
      return matchSearch && matchCity && matchDate;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'area_name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'city_name':
          aValue = (a.city_name || '').toLowerCase();
          bValue = (b.city_name || '').toLowerCase();
          break;
        case 'services_count':
          aValue = a.services_count || 0;
          bValue = b.services_count || 0;
          break;
        case 'vendors_count':
          aValue = a.vendors_count || 0;
          bValue = b.vendors_count || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return ' ↕️';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredAreas = getFilteredAndSortedAreas();

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>📍 Areas Management</h2>
        <button className="btn btn-primary" onClick={fetchAreas} style={{ backgroundColor: '#10b981' }}>
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filterSection}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search Area Name</label>
          <input
            type="text"
            placeholder="Type area name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.filterInput}
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Filter by City</label>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={styles.filterInput}
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city.id} value={city.id}>{city.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Filter by Date</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={styles.filterInput}
          />
        </div>

        <div style={styles.filterGroup}>
          <button
            onClick={() => {
              setSearchTerm('');
              setCityFilter('');
              setDateFilter('');
            }}
            style={{ ...styles.filterInput, cursor: 'pointer', backgroundColor: '#ef4444', color: 'white', border: 'none', fontWeight: 600 }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div style={styles.statsSummary}>
        <div style={styles.statsCard}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Total Areas</span>
          <p style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', margin: '4px 0' }}>{filteredAreas.length}</p>
        </div>
        <div style={styles.statsCard}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Total Services</span>
          <p style={{ fontSize: '24px', fontWeight: 700, color: '#4f46e5', margin: '4px 0' }}>
            {filteredAreas.reduce((sum, area) => sum + (area.services_count || 0), 0)}
          </p>
        </div>
        <div style={styles.statsCard}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Total Vendors</span>
          <p style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b', margin: '4px 0' }}>
            {filteredAreas.reduce((sum, area) => sum + (area.vendors_count || 0), 0)}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table" style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => toggleSort('area_name')}>
                Area Name {getSortIcon('area_name')}
              </th>
              <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => toggleSort('city_name')}>
                City {getSortIcon('city_name')}
              </th>
              <th style={{ ...styles.th, cursor: 'pointer', textAlign: 'center' }} onClick={() => toggleSort('services_count')}>
                Services {getSortIcon('services_count')}
              </th>
              <th style={{ ...styles.th, cursor: 'pointer', textAlign: 'center' }} onClick={() => toggleSort('vendors_count')}>
                Vendors {getSortIcon('vendors_count')}
              </th>
              <th style={{ ...styles.th, cursor: 'pointer', textAlign: 'center' }} onClick={() => toggleSort('created_at')}>
                Created {getSortIcon('created_at')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  ⏳ Loading areas...
                </td>
              </tr>
            ) : filteredAreas.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  📭 No areas found
                </td>
              </tr>
            ) : (
              filteredAreas.map((area, index) => (
                <tr key={area.id} style={{ ...styles.row, backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                  <td style={styles.td}>
                    <strong style={{ color: '#1f2937' }}>{area.name || 'N/A'}</strong>
                  </td>
                  <td style={styles.td}>
                    <span style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '4px 8px', borderRadius: '4px', fontSize: '13px', fontWeight: 600 }}>
                      {area.city_name || 'N/A'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{ backgroundColor: '#e0e7ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, display: 'inline-block' }}>
                      {area.services_count || 0}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{ backgroundColor: '#fef3c7', color: '#d97706', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, display: 'inline-block' }}>
                      {area.vendors_count || 0}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', fontSize: '13px', color: '#6b7280' }}>
                    {area.created_at ? new Date(area.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  filterSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
  },
  filterInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: '0.2s',
  },
  statsSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statsCard: {
    backgroundColor: '#f0fdf4',
    borderLeft: '4px solid #10b981',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #dcfce7',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  headerRow: {
    backgroundColor: '#f3f4f6',
    borderBottom: '2px solid #e5e7eb',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 700,
    color: '#374151',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  row: {
    borderBottom: '1px solid #e5e7eb',
    transition: '0.15s',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#1f2937',
  },
};

export default AdminAreas;
